import {
  isMetaApp,
  isFacebookApp,
  isInstagramApp,
} from "../contexts/device/browsers"

/**
 * Utility functions for detecting Meta in-app browsers and handling redirects
 */

export interface MetaAppInfo {
  isMetaApp: boolean
  isFacebookApp: boolean
  isInstagramApp: boolean
  shouldRedirect: boolean
}

/**
 * Detects if the user is accessing from a Meta app (Facebook, Instagram)
 * @param userAgent - The user agent string
 * @returns MetaAppInfo object with detection results
 */
export function detectMetaApp(userAgent: string): MetaAppInfo {
  const isFacebook = isFacebookApp(userAgent)
  const isInstagram = isInstagramApp(userAgent)
  const isMeta = isMetaApp(userAgent)

  return {
    isMetaApp: isMeta,
    isFacebookApp: isFacebook,
    isInstagramApp: isInstagram,
    shouldRedirect: isMeta, // Redirect if it's any Meta app
  }
}

/**
 * Generates the redirect URL to open in external browser
 * @param originalUrl - The original URL to redirect to
 * @param metaAppInfo - Information about the Meta app
 * @returns The redirect URL
 */
export function generateExternalBrowserUrl(
  originalUrl: string,
  metaAppInfo: MetaAppInfo
): string {
  // For Meta apps, we need to use specific URL schemes to open in external browser
  const baseUrl = originalUrl.replace(/^https?:\/\//, "")

  if (metaAppInfo.isFacebookApp || metaAppInfo.isInstagramApp) {
    // For Android: Create intent URL with multiple browser package options
    const androidIntentUrl = `intent://${baseUrl}#Intent;scheme=https;package=com.android.chrome;package=com.chrome.beta;package=com.chrome.dev;package=com.chrome.canary;package=org.mozilla.firefox;package=com.microsoft.emmx;package=com.opera.browser;package=com.opera.browser.beta;package=com.sec.android.app.sbrowser;package=mark.via.gp;package=org.mozilla.focus;package=com.brave.browser;package=com.kiwibrowser.browser;S.browser_fallback_url=${encodeURIComponent(originalUrl)};end;`

    return androidIntentUrl
  }

  // Fallback - just return the original URL
  return originalUrl
}

/**
 * Client-side redirect function to open URL in external browser
 * @param url - The URL to open
 * @param metaAppInfo - Information about the Meta app
 */
export function redirectToExternalBrowser(
  url: string,
  metaAppInfo: MetaAppInfo
): void {
  if (typeof window === "undefined") {
    return
  }

  try {
    if (metaAppInfo.isFacebookApp || metaAppInfo.isInstagramApp) {
      // Try to open in external browser using intent URL
      const externalUrl = generateExternalBrowserUrl(url, metaAppInfo)
      window.location.href = externalUrl

      // Fallback: If intent doesn't work, try opening in a new window
      setTimeout(() => {
        try {
          window.open(url, "_blank", "noopener,noreferrer")
        } catch (error) {
          console.warn("Failed to open in external browser:", error)
        }
      }, 1000)
    }
  } catch (error) {
    console.error("Error redirecting to external browser:", error)
    // Fallback to normal navigation
    window.location.href = url
  }
}

/**
 * Creates a deep link URL that will open in the default browser
 * @param url - The original URL
 * @returns Deep link URL
 */
export function createDeepLink(url: string): string {
  // For mobile devices, we can create intent URLs that will prompt to open in browser
  const baseUrl = url.replace(/^https?:\/\//, "")

  // Android intent URL that will open in default browser
  return `intent://${baseUrl}#Intent;scheme=https;package=com.android.chrome;package=com.chrome.beta;package=com.chrome.dev;package=com.chrome.canary;package=org.mozilla.firefox;package=com.microsoft.emmx;package=com.opera.browser;package=com.opera.browser.beta;package=com.sec.android.app.sbrowser;package=mark.via.gp;end;`
}

/**
 * Server-side function to check if request should be redirected
 * @param userAgent - Request user agent
 * @param url - Current URL
 * @returns Object with redirect information
 */
export function shouldRedirectRequest(userAgent: string, url: string) {
  const metaAppInfo = detectMetaApp(userAgent)

  if (!metaAppInfo.shouldRedirect) {
    return { shouldRedirect: false, redirectUrl: null, metaAppInfo }
  }

  // Don't redirect API routes or static assets
  if (
    url.includes("/api/") ||
    url.includes("/_next/") ||
    url.includes("/favicon")
  ) {
    return { shouldRedirect: false, redirectUrl: null, metaAppInfo }
  }

  // Create a redirect URL that will open in external browser
  const redirectUrl = generateExternalBrowserUrl(url, metaAppInfo)

  return {
    shouldRedirect: true,
    redirectUrl,
    metaAppInfo,
  }
}
